import React, { useState } from 'react';
import { css } from '@compiled/react';
import { Popup } from '@atlaskit/popup';

type Props = {
  content: string;
  /** Accessible label for the icon (e.g. "Atlassian Community profile Help Info") */
  iconLabel: string;
};

/**
 * Help tooltip: gray icon, on hover turns blue and shows tooltip above the icon.
 * Tooltip extends to the right, with spacing from the icon. Uses Popup for reliable positioning.
 */
export const FieldHelpTooltip: React.FC<Props> = ({ content, iconLabel }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span css={wrapperStyles}>
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="top-start"
        zIndex={500}
        fallbackPlacements={['top', 'top-end', 'bottom-start', 'bottom', 'bottom-end']}
        offset={[0, 12]}
        shouldFlip
        shouldFitViewport
        shouldDisableFocusLock
        content={() => (
          <div css={tooltipContentStyles} className="champion-form-tooltip-content" role="tooltip">
            {content}
          </div>
        )}
        trigger={(triggerProps) => (
          <button
            type="button"
            css={iconButtonStyles}
            ref={triggerProps.ref}
            aria-label={iconLabel}
            aria-controls={triggerProps['aria-controls']}
            aria-expanded={triggerProps['aria-expanded']}
            aria-haspopup={triggerProps['aria-haspopup']}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
          >
            <svg
              css={iconStyles}
              viewBox="0 0 24 24"
              focusable="false"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
              />
            </svg>
          </button>
        )}
      />
    </span>
  );
};

const wrapperStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: '0.25rem',
  verticalAlign: 'middle',
});

const iconButtonStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  margin: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#696969',
  ':hover': {
    color: '#16325c',
  },
  ':focus': {
    color: '#16325c',
    outline: '2px solid #16325c',
    outlineOffset: '2px',
  },
});

const iconStyles = css({
  width: '1rem',
  height: '1rem',
});

// Tooltip content: blue background, white text. Triangle points to icon (position set by global.css based on placement).
const tooltipContentStyles = css({
  position: 'relative',
  padding: '0.5rem 0.75rem',
  minWidth: '10rem',
  maxWidth: 'min(16rem, 80vw)',
  backgroundColor: '#16325c',
  color: '#ffffff',
  fontSize: '0.8125rem',
  fontWeight: 400,
  lineHeight: 1.5,
  fontFamily: 'sans-serif',
  borderRadius: '0.25rem',
  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  pointerEvents: 'none',
  /* Default (top-start): tooltip above icon, triangle at bottom pointing down, aligned with icon center (1rem icon = 0.5rem) */
  '::after': {
    content: '""',
    position: 'absolute',
    bottom: '-6px',
    left: '0.5rem',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '6px solid #16325c',
  },
});
